import React, { useState, useEffect } from 'react'
import { Box, Typography, Button, Paper, CircularProgress, Alert } from '@mui/material'
import MailTwoToneIcon from '@mui/icons-material/MailTwoTone'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { titleSx, descriptionSx, buttonSx, alertSx } from './ensureEmailsStyles'
import customToastWithAlert from 'src/common/components/CustomToastWithAlert'
import { alpha, styled } from '@mui/material/styles'
import { useGetAccountQuery } from '../../../../api/integrations'
import LoadingIndicator from '../../../../common/components/LoadingIndicator'
import useMutation from '../../../../hooks/useMutation'
import { useSendSimulationEmailMutation } from '../../../../api/onboardingApiSlice'

const mockData = {
  instructionsLink: 'https://helpcenter.cyberly.ai/category/16319-whitelisting-instructions',
  currentUserEmail: 'user@example.com'
}

export default function EnsureEmailsView({ onClose, currentStepDetails, completeStepAndProceed }) {
  const [testEmail, setTestEmail] = useState(mockData.currentUserEmail)
  const [emailSent, setEmailSent] = useState(false)
  const [emailError, setEmailError] = useState(null)
  const [resendTimer, setResendTimer] = useState(0)
  const { data, isLoading } = useGetAccountQuery()
  const { submit, isLoading: isSendingEmail } = useMutation(useSendSimulationEmailMutation, {
    successMessage: 'Email sent successfully'
  })
  useEffect(() => {
    if (data && data.email) {
      setTestEmail(data.email)
    }
  }, [data?.email])
  useEffect(() => {
    let interval
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer(prev => prev - 1)
      }, 1000)
    }
    return () => clearInterval(interval)
  }, [resendTimer])

  const handleViewInstructions = () => {
    window.open(mockData.instructionsLink, '_blank')
    customToastWithAlert({ message: 'Allowlisting instructions opened in a new tab.', type: 'info' })
  }

  const handleSendTestEmail = async () => {
    setEmailSent(false)
    setEmailError(null)
    await submit()
    setEmailSent(true)
    setResendTimer(60)
  }

  const handleSkip = () => {
    if (currentStepDetails && completeStepAndProceed) {
      completeStepAndProceed(currentStepDetails.id, { intent: 'skip' })
      onClose()
    } else {
      console.warn('EnsureEmailsView: Cannot skip step - missing props')
      onClose()
    }
  }

  const showSkipButton = !!currentStepDetails?.skipButtonText
  if (isLoading) {
    return <LoadingIndicator />
  }
  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%', boxSizing: 'border-box' }}>
      <Box sx={{ flexGrow: 1, overflowY: 'auto', mb: 2, pr: 1 }}>
        <SubStepPaper elevation={0}>
          <SubStepHeader>
            <StepNumber>{1}</StepNumber>
            <Typography sx={titleSx}>Allowlist Simulation Emails</Typography>
          </SubStepHeader>

          <Box>
            <Typography sx={descriptionSx}>
              Add our IPs and domains to your allowlist to ensure phishing simulations reach inboxes.
            </Typography>
            <Button variant='contained' size='small' onClick={handleViewInstructions} sx={buttonSx}>
              View instructions
            </Button>
          </Box>
        </SubStepPaper>

        <SubStepPaper elevation={0}>
          <SubStepHeader>
            <StepNumber>2</StepNumber>
            <Typography sx={titleSx}>Send a Test Email</Typography>
          </SubStepHeader>

          <Box>
            <Typography sx={descriptionSx}>
              Once you've correctly set up the rule, try it out by sending a test email to{' '}
              <strong>[{testEmail}]</strong>.
            </Typography>
            <Button
              variant='contained'
              size='small'
              onClick={handleSendTestEmail}
              disabled={isSendingEmail || resendTimer > 0 || !testEmail}
              startIcon={isSendingEmail ? <CircularProgress size={20} color='inherit' /> : null}
              sx={buttonSx}
            >
              {isSendingEmail
                ? 'Sending...'
                : resendTimer > 0
                  ? `Resend test email in ${resendTimer}s`
                  : 'Send test email'}
            </Button>
            {emailSent && (
              <Alert severity='success' icon={<MailTwoToneIcon fontSize='inherit' />} sx={alertSx('success')}>
                We've sent a test email to verify delivery. If the email lands in your inbox, everything is fine and
                you'll be able to confirm the configuration. If it's in spam, wait a bit and try again.
              </Alert>
            )}
            {emailError && (
              <Alert severity='error' icon={<InfoOutlinedIcon fontSize='inherit' />} sx={alertSx('error')}>
                {emailError}
              </Alert>
            )}
          </Box>
        </SubStepPaper>
      </Box>

      <Box
        sx={{
          mt: 'auto',
          pt: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          justifyContent: showSkipButton ? 'space-between' : 'flex-end',
          alignItems: 'center',
          gap: 1
        }}
      >
        {showSkipButton && (
          <Button variant='outlined' onClick={handleSkip} sx={buttonSx} fullWidth={true}>
            {currentStepDetails.skipButtonText}
          </Button>
        )}
      </Box>
    </Box>
  )
}
const SubStepPaper = styled(Paper)(({ theme, disabled }) => ({
  padding: theme.spacing(2, 2.5),
  marginBottom: theme.spacing(2),
  borderRadius: '12px',
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  transition: 'opacity 0.3s',
  ...(disabled && {
    opacity: 0.6,
    pointerEvents: 'none',
    backgroundColor: alpha(theme.palette.background.default, 0.5)
  })
}))

const SubStepHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  fontSize: '20px',
  fontWeight: '600',
  color: '#1B1B26',
  fontFamily: ' Plus Jakarta Sans',
  marginBottom: theme.spacing(1)
}))

const StepNumber = styled(Typography)(({ theme, disabled }) => ({
  width: '32px',
  height: '32px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: theme.spacing(1.5),
  fontSize: '16px',
  fontWeight: '600',
  color: '#fff',
  fontFamily: ' Plus Jakarta Sans',
  backgroundColor: ' #1B1B26',
  borderRadius: '50%',
  ...(disabled && {
    backgroundColor: '#E7E7EE',
    color: '#76767D'
  })
}))
